import { ShapeMapAdapter } from './ShapeMapAdapter';

export const SHAPE_POSITION_KEYS = ['x', 'y'] as const;
export const SHAPE_DIMENSION_KEYS = ['width', 'height', 'w', 'h'] as const;
export const SHAPE_GEOMETRY_KEYS = [...SHAPE_POSITION_KEYS, ...SHAPE_DIMENSION_KEYS] as const;

export type ShapeGeometryKey = (typeof SHAPE_GEOMETRY_KEYS)[number];

export class InvalidShapeGeometryError extends Error {
  constructor(
    readonly shapeId: string,
    readonly invalidKeys: readonly ShapeGeometryKey[],
  ) {
    super(`Shape ${shapeId} has invalid geometry: ${invalidKeys.join(', ')}`);
    this.name = 'InvalidShapeGeometryError';
  }
}

export function isValidShapeGeometryValue(key: ShapeGeometryKey, value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  return key === 'x' || key === 'y' ? true : value > 0;
}

export function getInvalidShapeCreationGeometry(
  shape: Record<string, unknown>,
): ShapeGeometryKey[] {
  const invalid: ShapeGeometryKey[] = [];

  for (const key of SHAPE_POSITION_KEYS) {
    if (!isValidShapeGeometryValue(key, shape[key])) invalid.push(key);
  }
  for (const key of SHAPE_DIMENSION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(shape, key) && shape[key] !== undefined
      && !isValidShapeGeometryValue(key, shape[key])) {
      invalid.push(key);
    }
  }

  return invalid;
}

/**
 * Filters a shape update at the persistence boundary. Invalid incoming
 * geometry never replaces the current value; unrelated and derived `rect*`
 * fields remain independent and pass through unchanged.
 */
export function normalizeShapeGeometryPatch(
  _current: Record<string, unknown>,
  incoming: Record<string, unknown>,
): { patch: Record<string, unknown>; rejectedKeys: ShapeGeometryKey[] } {
  const patch = { ...incoming };
  const rejectedKeys: ShapeGeometryKey[] = [];

  for (const key of SHAPE_GEOMETRY_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(incoming, key)) continue;
    if (isValidShapeGeometryValue(key, incoming[key])) continue;

    rejectedKeys.push(key);
    delete patch[key];
  }

  return { patch, rejectedKeys };
}

function shallowPatchValueEqual(current: unknown, incoming: unknown): boolean {
  if (Object.is(current, incoming)) return true;
  if (!current || !incoming || typeof current !== 'object' || typeof incoming !== 'object') return false;
  if (Array.isArray(current) || Array.isArray(incoming)) return false;

  const currentRecord = current as Record<string, unknown>;
  const incomingRecord = incoming as Record<string, unknown>;
  const currentKeys = Object.keys(currentRecord);
  const incomingKeys = Object.keys(incomingRecord);
  return currentKeys.length === incomingKeys.length
    && currentKeys.every((key) => Object.prototype.hasOwnProperty.call(incomingRecord, key)
      && Object.is(currentRecord[key], incomingRecord[key]));
}

/** Shape-only persistence boundary. ShapeMapAdapter intentionally stays generic. */
export class ShapePersistenceGateway {
  constructor(private readonly adapter: ShapeMapAdapter) {}

  get size(): number {
    return this.adapter.size;
  }

  get(id: string): Record<string, unknown> | undefined {
    return this.adapter.get(id);
  }

  has(id: string): boolean {
    return this.adapter.has(id);
  }

  forEach(fn: (value: Record<string, unknown>, key: string) => void): void {
    this.adapter.forEach(fn);
  }

  values(): Array<Record<string, unknown>> {
    return this.adapter.values();
  }

  delete(id: string): void {
    this.adapter.delete(id);
  }

  /**
   * Replace a persisted shape through the canonical adapter. The adapter
   * performs a diff-aware write, so callers can update text/properties without
   * routing the operation through geometry validation intended for patches.
   */
  set(id: string, shape: Record<string, unknown>): void {
    this.adapter.set(id, shape);
  }

  clear(): void {
    this.adapter.raw.clear();
  }

  validateCreates(shapes: readonly Record<string, unknown>[]): void {
    for (const shape of shapes) this.assertCreatable(shape);
  }

  create(shape: Record<string, unknown>): void {
    const id = this.assertCreatable(shape);
    this.adapter.set(id, shape);
  }

  upsert(shape: Record<string, unknown>): ShapeGeometryKey[] {
    const id = typeof shape.id === 'string' ? shape.id : '';
    if (!id) throw new InvalidShapeGeometryError('<missing-id>', []);
    const current = this.adapter.get(id);
    if (!current) {
      this.create(shape);
      return [];
    }
    const { patch: replacement, rejectedKeys } = normalizeShapeGeometryPatch(current, shape);
    for (const key of rejectedKeys) {
      if (Object.prototype.hasOwnProperty.call(current, key)) replacement[key] = current[key];
    }
    this.adapter.set(id, replacement);
    return rejectedKeys;
  }

  patch(id: string, incoming: Record<string, unknown>): ShapeGeometryKey[] {
    const current = this.adapter.get(id);
    if (!current) return [];
    const { patch, rejectedKeys } = normalizeShapeGeometryPatch(current, incoming);
    const changedPatch = Object.fromEntries(Object.entries(patch).filter(([key, value]) => {
      if (value === undefined) return Object.prototype.hasOwnProperty.call(current, key);
      return !shallowPatchValueEqual(current[key], value);
    }));
    if (Object.keys(changedPatch).length > 0) this.adapter.patch(id, changedPatch);
    return rejectedKeys;
  }

  private assertCreatable(shape: Record<string, unknown>): string {
    const id = typeof shape.id === 'string' ? shape.id : '';
    const invalidKeys = getInvalidShapeCreationGeometry(shape);
    if (!id || invalidKeys.length > 0) {
      throw new InvalidShapeGeometryError(id || '<missing-id>', invalidKeys);
    }
    return id;
  }
}
